import React from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import MaintenanceGuides from 'src/components/MaintenanceGuides/MaintenanceGuides';
import { MaintenanceGuide } from 'src/SharedCustomHooks/UseMaintenanceGuides/types';
import LoadState from 'src/components/LoadState/LoadState';

interface AssetMaintainenceGuideProps {
  asset: any;
  maintenanceGuides: MaintenanceGuide[];
  loading: boolean;
  saveGuide: (guide: Partial<MaintenanceGuide>) => Promise<void>;
  updateGuide: (guide: MaintenanceGuide) => Promise<void>;
  deleteGuide: (guide: MaintenanceGuide) => Promise<void>;
  error: any;
  setSearchTerm: (term: string) => void;
  searchTerm: string
}

const AssetMaintainenceGuide = ({
  asset,
  maintenanceGuides,
  loading,
  saveGuide,
  updateGuide,
  deleteGuide,
  error,
  setSearchTerm,
  searchTerm
}: AssetMaintainenceGuideProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const handleSaveMaintenanceGuide = async (maintenanceGuide: any) => {
    if (maintenanceGuide.id) {
      await updateGuide({
        ...maintenanceGuide,
        itemId: asset.id,
      });
    } else {
      await saveGuide({
        ...maintenanceGuide,
        author: {
          id: user.id,
          firstName: user.firstname,
          lastName: user.lastname,
        },
        createdAt: new Date().toISOString(),
      });
    }
  };

  const handleDeleteNote = async (guide: MaintenanceGuide) => {
    await deleteGuide(guide);
  };

  const ROLES_PERMITTED_TO_ADD_MAINTENANCE_REMINDER = new Set([
    'Super Admin',
    'Admin',
    'Property Developer',
  ]);
  const { roles } = user;

  const canEdit: any = roles.some((role: any) =>
    ROLES_PERMITTED_TO_ADD_MAINTENANCE_REMINDER.has(role.role.name)
  );

  if (error) {
    return (
      <div className="error-state">
        <p>Failed to load maintenance reminders.</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <LoadState isLoading={loading}>
      <MaintenanceGuides
        from="Asset"
        breadcrumb={asset.item.name + ' in ' + asset.roomType}
        maintenanceguides={maintenanceGuides}
        onlyView={!canEdit}
        onSaveMaintenanceGuide={handleSaveMaintenanceGuide}
        onDeleteNote={(guide: any) =>
          handleDeleteNote({ ...guide, Type: 'MAINTENANCEGUIDE' })
        }
        onSearch={(e: any) => setSearchTerm(e.target.value.trim())}
        searchTerm={searchTerm}
      />
    </LoadState>
  );
};

export default AssetMaintainenceGuide;
